import { storage } from '@abyss/web/tools/storage';
import { createTheme } from '@abyss/web/tools/theme';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { PrintProvider } from '@abyss/web/ui/PrintProvider';
import { RouterProvider } from '@abyss/web/ui/RouterProvider';
import { ThemeProvider } from '@abyss/web/ui/ThemeProvider';
import { ToastProvider } from '@abyss/web/ui/Toast';
import { useEffect, useState } from 'react';

import { Constants } from '../../common/Constants';
import { LDProvider } from '../../common/LDProvider/LDProvider';
import { PortalData, PortalName } from '../../common/Portal';
import { CountySearchContextProvider } from '../../context/CountySearchContext';
import { PortalContextProvider } from '../../context/PortalContext';
import { SearchFilterContextProvider } from '../../context/SearchFilterContext';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { useCurrentMember } from '../../hooks/useCurrentMember/useCurrentMember';
import { registerEvents } from '../../tools/events';
import { ConditionalWrapper } from '../../utils/portal.utils';
import { callGuestSessionUrl } from '../../utils/session.utils';
import { Analytics } from '../ProviderSearch/context/Analytics';
import { Configuration } from '../ProviderSearch/context/Configuration';
import { Internationalization } from '../ProviderSearch/context/Internationalization';
import { SearchLocation } from '../ProviderSearch/context/SearchLocation';
import { createRouter } from '../ProviderSearchForPortal/createRouter';
import { optumThemeOverride } from '../themeOverrides/optumThemeOverride';
import { uhcThemeOverride } from '../themeOverrides/uhcThemeOverride';
import { GuestRoutes } from './GuestRoutes';
import { GuestIFPTimeoutModal, GuestTimeoutModalWrapper } from './modals';
import { isLawwGuest } from './utils';

export const GuestApp = () => {
  // Select theme based on portalName: LAWW uses Optum theme, others use UHC theme
  const isLAWW = isLawwGuest();
  const themeName = isLAWW ? 'optum' : 'uhc';
  const themeOverride = isLAWW ? optumThemeOverride : uhcThemeOverride;

  // @ts-expect-error - createTheme type definition doesn't match the actual implementation
  const theme = createTheme(themeName, themeOverride);
  const { isIFPEarlyPay = false } = useCurrentMember() || {};
  return (
    <Internationalization>
      <Analytics>
        <PrintProvider>
          <OverlayProvider>
            <ThemeProvider theme={theme}>
              {isIFPEarlyPay ? (
                <GuestIFPTimeoutModal />
              ) : (
                <GuestTimeoutModalWrapper />
              )}
              <ToastProvider containerWidth="24rem" />
              <SearchFilterContextProvider>
                <CountySearchContextProvider>
                  <ConditionalWrapper
                    isCondition={isIFPEarlyPay}
                    wrapper={(children) => (
                      <SearchLocation>{children}</SearchLocation>
                    )}
                  >
                    <GuestRoutes />
                  </ConditionalWrapper>
                </CountySearchContextProvider>
              </SearchFilterContextProvider>
            </ThemeProvider>
          </OverlayProvider>
        </PrintProvider>
      </Analytics>
    </Internationalization>
  );
};

export const GuestExperience = (args) => {
  let portalData: PortalData | undefined;
  try {
    portalData = args?.['portal-data']
      ? JSON.parse(args['portal-data'])
      : undefined;
  } catch {
    portalData = undefined;
  }
  const basePath =
    portalData?.portalConfig?.basePath ||
    args?.basePath ||
    (typeof window !== 'undefined' &&
      window.location.pathname.replace(/\/$/, '')) ||
    '/guest';
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const router = createRouter(GuestApp, basePath);

  useEffect(() => {
    registerEvents(undefined, true);

    const useGuestPersistence =
      basePath === '/guest-plan-selection' || isLawwGuest();
    const guestSessionUrl = portalData?.portalConfig?.guestSessionUrl;
    callGuestSessionUrl(useGuestPersistence, guestSessionUrl).then(() => {
      setIsLoading(false);
    });
  }, [basePath]);

  useEffect(() => {
    if (portalData && portalData.portalName === PortalName.LAWW_GUEST) {
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION.PORTAL_NAME,
        PortalName.LAWW_GUEST.toLowerCase()
      );
    }
  }, [portalData]);

  if (isLoading) {
    return (
      <LoadingSpinner
        ariaLoadingLabel="loading results"
        data-testid="loading-spinner"
        isLoading={isLoading}
        size="$lg"
      />
    );
  }

  const content = (
    <Configuration>
      <LDProvider>
        <RouterProvider router={router} />
      </LDProvider>
    </Configuration>
  );

  if (portalData) {
    return (
      <ErrorBoundary>
        <PortalContextProvider portalDataArgs={portalData}>
          {content}
        </PortalContextProvider>
      </ErrorBoundary>
    );
  }

  return content;
};
